import { useRef, useState, type DragEvent } from "react";
import { UploadCloud } from "lucide-react";
import { cn } from "@/lib/utils";
import { BusyIndicator } from "@/components/BusyIndicator";
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_FILES,
  ATTACHMENT_MAX_FILE_BYTES,
  isAllowedAttachment,
} from "@/features/conversations/attachments";
import { formatFileSize } from "@/utils/files";

export function validateFiles(
  files: File[],
  existingCount = 0,
): { accepted: File[]; errors: string[] } {
  const accepted: File[] = [];
  const errors: string[] = [];
  for (const f of files) {
    if (!isAllowedAttachment(f.name)) {
      errors.push(`${f.name} — unsupported file type.`);
      continue;
    }
    if (f.size > ATTACHMENT_MAX_FILE_BYTES) {
      errors.push(`${f.name} — larger than ${formatFileSize(ATTACHMENT_MAX_FILE_BYTES)}.`);
      continue;
    }
    if (existingCount + accepted.length >= ATTACHMENT_MAX_FILES) {
      errors.push(`${f.name} — limit of ${ATTACHMENT_MAX_FILES} files reached.`);
      continue;
    }
    accepted.push(f);
  }
  return { accepted, errors };
}

export function AttachmentDropzone({
  onFiles,
  disabled,
  busy,
  className,
}: {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  busy?: boolean;
  className?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  function take(list: FileList | null) {
    const files = Array.from(list ?? []);
    if (files.length) onFiles(files);
  }

  function onDrop(e: DragEvent<HTMLButtonElement>) {
    e.preventDefault();
    setOver(false);
    if (disabled) return;
    take(e.dataTransfer.files);
  }

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        aria-busy={busy}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!disabled && !busy) setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        className={cn(
          "flex w-full flex-col items-center justify-center gap-1 rounded-md border px-4 py-8 text-center transition-colors",
          busy
            ? "border-solid border-primary bg-primary/10 cursor-wait"
            : cn(
                "border-dashed",
                over ? "border-primary bg-primary/5" : "border-input hover:border-slate-400",
                disabled && "cursor-not-allowed opacity-60",
              ),
          className,
        )}
      >
        {busy ? (
          <BusyIndicator label="Uploading…" />
        ) : (
          <>
            <UploadCloud className="mb-1 h-6 w-6 text-slate-400" />
            <span className="text-sm text-slate-600">Drop files here or click to upload</span>
            <span className="text-xs text-slate-400">
              Images, video, PDF, Office documents, text and archives up to{" "}
              {formatFileSize(ATTACHMENT_MAX_FILE_BYTES)} each, {ATTACHMENT_MAX_FILES} files max
            </span>
          </>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        className="hidden"
        onChange={(e) => { take(e.target.files); e.target.value = ""; }}
      />
    </>
  );
}
